import { useRef } from "react";
import * as yup from 'yup';
import { Formik } from 'formik';
import { Button as BSButton, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import { AddressService } from "../../../services";
import {
  AddressEditor,
  addressValidationSchema,
  Button,
  FormSectionDivider,
  FormSectionHeader,
  HandleDropDownListChange,
  SlideOutModal,
  SlideOutModalProps,
  SearchableOrganizationDropdown
} from "../../../components";
import { useSelector } from "react-redux";
import { getUserInfo, isSysAdmin, isTenantAdmin } from "../../../app";

export interface AddLocationModalProps extends SlideOutModalProps {
  onSubmit: any;
}

export function AddLocationModal(props: Readonly<AddLocationModalProps>) {

  const submitRef = useRef<HTMLButtonElement>(null);
  const userOrg = useSelector(getUserInfo())?.organizationIds ?? [];
  const sysAdmin = useSelector(isSysAdmin());
  const tenantAdmin = useSelector(isTenantAdmin());

  const filterOrg = (sysAdmin || tenantAdmin) ? [] : userOrg;


  const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    locationCode: yup.string().required("Location Code is required"),
    businessCode: yup.string().notRequired(),
    organizationId: yup.number().required("Organization is required"),
    address: addressValidationSchema,
  });

  return (
    <SlideOutModal {...props} title="Add New Location"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100">
                Add
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          name: '',
          locationCode: '',
          businessCode: '',
          organizationId: null,
          address: {
            streetAddress: '',
            streetAddress2: '',
            city: '',
            postalCode: '',
            regionId: 1,
          },
        }}
        onSubmit={async (values) => {
          const address = {
            id: 0,
            streetAddress: values.address.streetAddress,
            streetAddress2: values.address.streetAddress2,
            city: values.address.city,
            postalCode: values.address.postalCode,
            regionId: values.address.regionId,
          };

          const addressService = new AddressService();
          const result = await addressService.insert(address);

          const location = {
            name: values.name,
            locationCode: values.locationCode,
            businessCode: values.businessCode,
            organizationId: values.organizationId,
            addressId: result.id,
            address: result
          };
          props.onSubmit(location);
        }}
      >
        {({ values, handleSubmit, handleChange, setFieldValue, touched, errors }) => {
          return (
            <Form onSubmit={handleSubmit} className="px-2 pt-2">

              {/* Location details */}
              <FormSectionHeader>Location Details</FormSectionHeader>
              <Row>
                <Col>
                  <FloatingLabel label="Name" className="mb-3">
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder="Location Name"
                      value={values.name}
                      onChange={handleChange}
                      isInvalid={touched.name && !!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              </Row>
              <Row>
                <Col xs={24} md={12}>
                  <FloatingLabel label="Location Code" className="mb-3">
                    <Form.Control
                      type="text"
                      name="locationCode"
                      placeholder="Location Code"
                      value={values.locationCode}
                      onChange={handleChange}
                      isInvalid={touched.locationCode && !!errors.locationCode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.locationCode}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
                <Col xs={24} md={12}>
                  <FloatingLabel label="Business Code" className="mb-3">
                    <Form.Control
                      type="text"
                      name="businessCode"
                      placeholder="Business Code"
                      value={values.businessCode}
                      onChange={handleChange}
                      isInvalid={touched.businessCode && !!errors.businessCode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.businessCode}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="organizationId">
                    <SearchableOrganizationDropdown
                      filterOrg={filterOrg}
                      name="organizationId"
                      value={values.organizationId}
                      onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                      isInvalid={touched.organizationId && !!errors.organizationId}
                    >
                      <Form.Control.Feedback type="invalid">
                        {errors.organizationId}
                      </Form.Control.Feedback>
                    </SearchableOrganizationDropdown>
                  </Form.Group>
                </Col>
              </Row>

              <FormSectionDivider />

              {/* Address */}
              <FormSectionHeader>Address</FormSectionHeader>
              <AddressEditor/>

              <BSButton ref={submitRef} type="submit" className="d-none" />
            </Form>
          )
        }}
      </Formik>
    </SlideOutModal>
  );
}

export default AddLocationModal;
